/**
 * Business Rules List View
 * Displays ALL rules (system + tenant + orgUnit) with source indicator
 * System rules are read-only and can be overridden by creating a tenant rule with the same ruleKey
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPremium, GridColDef, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Edit, Delete, Add, ContentCopy, Lock, LockOpen } from '@mui/icons-material';
import { Box, Typography, Button, Chip, Link, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { ViewToolbar } from '@/components/ViewToolbar';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { formatDateTime } from '@/lib/locale';
import { useGridStatePersistence } from '@/lib/useGridStatePersistence';
import { businessRulesService, UnifiedRule } from '@/services/core/BusinessRulesService';
import { RuleSeverityChip } from '@/core/rules/components/RuleSeverityChip';
import { buildOverridePreFill } from '@/core/rules/overridePreFill';
import { describeRunsOn } from '@/core/rules/runsOn';
import { businessRuleService } from '@/services/core/BusinessRuleService';

/**
 * Get chip color for source badge
 */
const getSourceColor = (source: string): 'primary' | 'secondary' | 'default' | 'info' => {
  switch (source) {
    case 'System':
      return 'primary';
    case 'Tenant':
      return 'secondary';
    case 'OrgUnit':
      return 'info';
    default:
      return 'default';
  }
};

/**
 * Get chip color for status badge
 */
const getStatusColor = (status?: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'PENDING_APPROVAL':
      return 'warning';
    case 'REJECTED':
      return 'error';
    default:
      return 'default';
  }
};

export const BusinessRulesListView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiRef = useGridApiRef();
  // Same device-local layout persistence as every other grid (column widths/order/
  // visibility, sorting, pagination, density). Distinct key from the generated
  // BusinessRule list — this page shows the unified system+tenant view.
  useGridStatePersistence(apiRef, 'Core', 'BusinessRules');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<UnifiedRule[]>([]);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);
  const [selectedSystemRule, setSelectedSystemRule] = useState<UnifiedRule | null>(null);

  const loadRules = async () => {
    setLoading(true);
    setApiValidationErrors([]);
    try {
      const response = await businessRulesService.getAllRules();
      setRows(response.items);
    } catch (error) {
      const errors = getValidationErrors(error);
      if (errors.length > 0) {
        setApiValidationErrors(errors);
      } else {
        setApiValidationErrors([{ field: 'grid', message: t('businessRules.loadFailed'), instancePath: '#', members: [] }]);
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleRefresh = () => {
    loadRules();
  };

  const handleDelete = async (id: string, source: string) => {
    if (source === 'System') {
      setApiValidationErrors([{ field: 'delete', message: t('businessRules.systemNotDeletable'), instancePath: '#', members: [] }]);
      return;
    }
    if (confirm(`Are you sure you want to delete this rule?`)) {
      try {
        setApiValidationErrors([]);
        await businessRuleService.delete(id);
        handleRefresh();
      } catch (error) {
        const errors = getValidationErrors(error);
        if (errors.length > 0) {
          setApiValidationErrors(errors);
        } else {
          setApiValidationErrors([{ field: 'delete', message: t('businessRules.deleteFailed'), instancePath: '#', members: [] }]);
        }
      }
    }
  };

  const handleCreateOverride = (rule: UnifiedRule) => {
    // Navigate to create form with pre-filled data from system rule
    navigate('/core/businessrules/new', { state: { preFill: buildOverridePreFill(rule) } });
  };

  const columns: GridColDef<UnifiedRule>[] = [
    {
      field: '_rowActions',
      type: 'actions',
      headerName: t('common.actions'),
      width: 120,
      getActions: (params) => {
        const isSystem = params.row.source === 'System';
        const actions = [];

        if (isSystem) {
          // System rules: Edit (readonly detail view) + Create Override
          actions.push(
            <GridActionsCellItem
              icon={<Edit />}
              label={t('businessRules.viewDetails')}
              onClick={() => navigate(`/core/businessrules/system/${params.id}`, {
                state: { systemRule: params.row }
              })}
            />
          );
          if (params.row.canBeOverridden) {
            actions.push(
              <Tooltip title={t('businessRules.createOverride')}>
                <GridActionsCellItem
                  icon={<ContentCopy />}
                  label={t('businessRules.createOverride')}
                  onClick={() => handleCreateOverride(params.row)}
                />
              </Tooltip>
            );
          }
        } else {
          // Tenant/OrgUnit rules: Edit + Delete
          actions.push(
            <GridActionsCellItem
              icon={<Edit />}
              label={t('common.edit')}
              onClick={() => navigate(`/core/businessrules/${params.id}`)}
            />
          );
          actions.push(
            <GridActionsCellItem
              icon={<Delete />}
              label={t('common.delete')}
              onClick={() => handleDelete(params.id as string, params.row.source)}
            />
          );
        }

        return actions;
      }
    },
    {
      field: 'source',
      headerName: t('businessRules.colSource'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getSourceColor(params.value)}
          icon={params.value === 'System' ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
        />
      )
    },
    {
      field: 'ruleNumber',
      headerName: t('DisplayName:BusinessRule.ruleNumber'),
      width: 120
    },
    {
      field: 'orgUnitName',
      headerName: t('DisplayName:BusinessRule.orgUnitId'),
      width: 150,
      renderCell: (params) => {
        const orgUnitId = params.row.orgUnitId;
        if (!orgUnitId) {
          return <span style={{ color: '#888' }}>—</span>;
        }
        return (
          <Link
            component={RouterLink}
            to={`/core/orgunits/${orgUnitId}`}
            state={{ returnTo: location.pathname + location.search }}
            onClick={(e) => e.stopPropagation()}
            underline="always"
          >
            {params.value || ''}
          </Link>
        );
      }
    },
    {
      field: 'module',
      headerName: t('DisplayName:BusinessRule.module'),
      width: 120
    },
    {
      field: 'entity',
      headerName: t('DisplayName:BusinessRule.entity'),
      width: 120
    },
    {
      field: 'ruleKey',
      headerName: t('DisplayName:BusinessRule.ruleKey'),
      width: 200
    },
    {
      field: 'name',
      headerName: t('DisplayName:BusinessRule.name'),
      width: 250
    },
    {
      field: 'description',
      headerName: t('DisplayName:BusinessRule.description'),
      width: 300
    },
    {
      field: 'enabled',
      headerName: t('DisplayName:BusinessRule.enabled'),
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common.yes') : t('common.no')}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      )
    },
    {
      field: 'priority',
      headerName: t('DisplayName:BusinessRule.priority'),
      width: 90,
      type: 'number'
    },
    {
      field: 'status',
      headerName: t('DisplayName:BusinessRule.status'),
      width: 130,
      renderCell: (params) => {
        if (!params.value) return null;
        return (
          <Chip
            label={params.value.replace('_', ' ')}
            size="small"
            color={getStatusColor(params.value)}
          />
        );
      }
    },
    {
      field: 'isOverride',
      headerName: t('businessRules.colOverride'),
      width: 100,
      renderCell: (params) => {
        if (params.row.source === 'System') return null;
        return params.value ? (
          <Chip label={t('common.yes')} size="small" color="warning" />
        ) : null;
      }
    },
    {
      field: 'canBeOverridden',
      headerName: t('businessRules.colOverridable'),
      width: 110,
      renderCell: (params) => {
        if (params.row.source !== 'System') return null;
        return params.value ? (
          <Chip label={t('common.yes')} size="small" color="success" />
        ) : (
          <Chip label={t('common.no')} size="small" color="error" />
        );
      }
    },
    {
      // C8: the consequence, visible by default — an advisory rule must read as one in the grid
      field: 'errorType',
      headerName: t('DisplayName:BusinessRule.errorType'),
      width: 120,
      renderCell: (params) => <RuleSeverityChip severity={params.value as string | undefined} />,
    },
    {
      field: 'errorMessage',
      headerName: t('DisplayName:BusinessRule.errorMessage'),
      width: 200
    },
    {
      field: 'creationTime',
      headerName: t('grid.created'),
      type: 'dateTime',
      width: 180,
      valueGetter: (_value, row) => (row.creationTime ? new Date(row.creationTime) : null),
      valueFormatter: (value: Date | null) => (value ? formatDateTime(value) : '')
    },
    {
      field: 'creatorName',
      headerName: t('grid.createdBy'),
      width: 180
    },
    {
      field: 'lastModificationTime',
      headerName: t('grid.modified'),
      type: 'dateTime',
      width: 180,
      valueGetter: (_value, row) => (row.lastModificationTime ? new Date(row.lastModificationTime) : null),
      valueFormatter: (value: Date | null) => (value ? formatDateTime(value) : '')
    },
    {
      field: 'lastModifierName',
      headerName: t('grid.modifiedBy'),
      width: 180
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Error handling */}
      <ValidationErrorBanner
        errors={apiValidationErrors}
        onExpand={() => setValidationPanelOpen(true)}
        onClear={() => {
          setApiValidationErrors([]);
          setValidationPanelOpen(false);
        }}
      />

      {/* Toolbar with actions on left */}
      <ViewToolbar onRefresh={handleRefresh}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate(`/core/businessrules/new`)}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
          }}
        >
          {t('businessRules.newRule')}
        </Button>
        <Button
          startIcon={<Delete />}
          disabled
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
          }}
        >
          {t('common.delete')}
        </Button>
      </ViewToolbar>

      {/* Main Content Area with Drawer */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
            {t('businessRules.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('businessRules.subtitle')}
          </Typography>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <DataGridPremium
              apiRef={apiRef}
              density="compact"
              rowHeight={GRID_ROW_HEIGHT}
              columns={columns}
              rows={rows}
              rowCount={rows.length}
              loading={loading}
              pagination
              onRowClick={(params) => {
                if (params.row.source === 'System') {
                  setSelectedSystemRule(params.row);
                } else {
                  navigate(`/core/businessrules/${params.id}`);
                }
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 }
                },
                sorting: {
                  sortModel: [{ field: 'priority', sort: 'asc' }]
                },
                // Audit columns are opt-in via the column picker, same as generated list views.
                columns: {
                  columnVisibilityModel: {
                    creationTime: false,
                    creatorName: false,
                    lastModificationTime: false,
                    lastModifierName: false
                  }
                }
              }}
              disableRowSelectionOnClick
              sx={{
                flex: 1,
                minHeight: 0,
                '& .MuiDataGrid-row': {
                  cursor: 'pointer'
                },
                '& .MuiDataGrid-row[data-rowindex]:has([data-field="source"] .MuiChip-colorPrimary)': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            />
          </Box>
        </Box>

        {/* Validation Error Panel - right side slide-out */}
        <ValidationErrorPanel
          open={validationPanelOpen}
          onClose={() => setValidationPanelOpen(false)}
          errors={apiValidationErrors}
        />
      </Box>

      {/* System Rule Detail Dialog */}
      <Dialog
        open={!!selectedSystemRule}
        onClose={() => setSelectedSystemRule(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Lock fontSize="small" color="primary" />
          {t('businessRules.systemRuleTitle', { name: selectedSystemRule?.name ?? '' })}
          {/* C8: an advisory rule announces itself in the title, not only in a row below the fold */}
          {selectedSystemRule && <RuleSeverityChip severity={selectedSystemRule.errorType} />}
        </DialogTitle>
        <DialogContent dividers>
          {selectedSystemRule && (
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 150 }}>{t('DisplayName:BusinessRule.ruleNumber')}</TableCell>
                  <TableCell>{selectedSystemRule.ruleNumber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.ruleKey')}</TableCell>
                  <TableCell><code>{selectedSystemRule.ruleKey}</code></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.module')}</TableCell>
                  <TableCell>{selectedSystemRule.module}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.entity')}</TableCell>
                  <TableCell>{selectedSystemRule.entity}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.description')}</TableCell>
                  <TableCell>{selectedSystemRule.description || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.priority')}</TableCell>
                  <TableCell>{selectedSystemRule.priority}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.enabled')}</TableCell>
                  <TableCell>{selectedSystemRule.enabled ? t('common.yes') : t('common.no')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('businessRules.canBeOverridden')}</TableCell>
                  <TableCell>{selectedSystemRule.canBeOverridden ? t('common.yes') : t('common.no')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.errorType')}</TableCell>
                  <TableCell><RuleSeverityChip severity={selectedSystemRule.errorType} /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.errorMessage')}</TableCell>
                  <TableCell>{selectedSystemRule.errorMessage || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('businessRules.runsOn')}</TableCell>
                  <TableCell>{describeRunsOn(selectedSystemRule, t)}</TableCell>
                </TableRow>
                {selectedSystemRule.when && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.when')}</TableCell>
                    <TableCell><pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{selectedSystemRule.when}</pre></TableCell>
                  </TableRow>
                )}
                {selectedSystemRule.expression && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t('DisplayName:BusinessRule.expression')}</TableCell>
                    <TableCell><pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{selectedSystemRule.expression}</pre></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          {selectedSystemRule?.canBeOverridden && (
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => {
                if (selectedSystemRule) {
                  handleCreateOverride(selectedSystemRule);
                  setSelectedSystemRule(null);
                }
              }}
            >
              {t('businessRules.createOverride')}
            </Button>
          )}
          <Button onClick={() => setSelectedSystemRule(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
